// ─── Grouped bars from a second dimension (the "missing pivot" rule) ─────────
// A chart tile draws ONE x-axis category per row and one series per measure ×
// pivot. That's right for a Looker query that actually pivots — but a query with
// TWO row dimensions and no pivot ("Tickets Sold by Age Group" × event, for a
// YoY comparison) has one row per age-group × event pair, so the same age group
// arrives several times and the x-axis repeats it instead of standing the two
// years side by side. The numbers are all there; they're just unreadable.
//
// So: when a result has no pivots but a dimension is repeating the axis, fold
// the extra dimension into the pivoted SHAPE Looker would have returned had the
// tile been pivoted — measure cells keyed by series value, one row per x
// category. Everything downstream (series building, legend, tooltips, value
// labels, per-series types, drill links) then works unchanged, because it is
// the shape the pivot path already handles.
//
// The ONE promise this module makes: a chart never prints the same x-axis
// category twice. When the extra dimension can be folded into a legend it is;
// when it can't, the leftover dimensions at least combine into one axis label,
// so two bars can never wear the same name. Three things had to be understood
// for that to hold on real tiles:
//   · a dimension Looker PIVOTED is still listed in fields.dimensions, but its
//     values live in the column groups — it is neither an axis nor a row key;
//   · a YoY query usually carries the event TWICE (its name and its date, or
//     its year), and a second description of the series re-splits every
//     category if it's left on the axis — so drop what the series determines;
//   · a dimension HIDDEN in the vis is still in the result, and grouping by it
//     beats drawing the same category twice.
//
// Pure rules, no React — unit-tested in test/chartPivot.test.js.

// A legend of 13+ entries is unreadable on a phone; past that, leave the tile
// as it was rather than swapping one unreadable chart for another.
export const MAX_AUTO_SERIES = 12;
// A date-ish dimension belongs on the x-axis (a time series), NOT in the legend
// — unless there are only a handful of them, which is a period comparison
// (2025 vs 2026) and exactly what should be grouped.
export const MAX_DATE_SERIES = 4;

const DATEY = /date|time|month|week|quarter|year|day|hour|minute/i;
// The keys a json_detail LEAF cell carries — a series can't be named after one.
const LEAF_KEYS = new Set(['value', 'rendered', 'rendered_value', 'links']);

// A LEAF cell ({ value, rendered, links }) as opposed to a pivoted cell MAP
// ({ pivotKey: leafCell, … }).
function isLeafCell(c) {
  return !!c && typeof c === 'object' && !Array.isArray(c) && ('value' in c || 'rendered' in c || 'rendered_value' in c);
}
// Same text a chart axis would show for a cell (mirrors cellText in format.js),
// so the series key and the label a viewer reads are the same string.
function text(cell) {
  if (cell == null) return '∅';
  if (cell.rendered != null && cell.rendered !== '') return String(cell.rendered);
  if (cell.value == null) return '∅';
  return String(cell.value);
}
const dateLike = (d) => DATEY.test(String(d?.type || '')) || DATEY.test(String(d?.name || ''));
const dimLabel = (d) => d?.label_short || d?.label || d?.name || '';

// Name of the synthetic x dimension used when more than one dimension is left
// over — its cells carry the combined label so the axis stays unambiguous.
export const COMBINED_X = '__pulse_x__';

// The dimensions that actually live in the ROWS. A dimension Looker pivoted is
// reported in `fields.dimensions` but its values are in the column groups, so
// reading it as the axis draws a chart with no labels at all (mirrors
// rowDimensions in lib/lookerOrder.js). A handful of rows is enough to tell:
// a json_detail row always carries every row dimension.
function rowDimensions(rows, dimensions) {
  const sample = rows.slice(0, 5).filter((r) => r && typeof r === 'object');
  if (!sample.length) return [];
  return (dimensions || []).filter((d) => d?.name
    && sample.some((r) => Object.prototype.hasOwnProperty.call(r, d.name)));
}

const labelsOf = (rows, d) => rows.map((r) => text(r[d.name]));
const distinct = (list) => new Set(list).size;
const keyOf = (r, dims) => dims.map((d) => text(r[d.name])).join(' · ');

// Is `d`'s value fixed by `s`'s? A YoY query names the event twice — "Kappa
// 2026" and its start date, or its year — and a second description of the same
// thing re-splits every category if it stays on the axis (which is exactly why
// "8 age groups × 2 events" could still arrive as 16 distinct axis slots). A
// dimension with one value throughout is caught by the same test: also nothing
// the axis can say.
function determinedBy(rows, s, d) {
  const seen = new Map();
  for (const r of rows) {
    const k = text(r[s.name]);
    const v = text(r[d.name]);
    if (!seen.has(k)) seen.set(k, v);
    else if (seen.get(k) !== v) return false;
  }
  return true;
}

// One synthetic axis column carrying the combined label, so two rows can never
// print the same name.
const combinedDim = (dims) => ({
  name: COMBINED_X,
  label: dims.map(dimLabel).join(' · '),
  label_short: dims.map(dimLabel).join(' · '),
  type: 'string',
});

// Which dimension should become the series (the legend), and what's left for
// the axis? The legend goes to the dimension with the FEWEST distinct values —
// fewer bars per group reads better, and it's what a person pivots by (2 events
// across 8 age groups, not 8 age groups across 2 events). `candidates` are the
// dimensions allowed to become the legend; `axisDims` the ones that can carry
// the axis (a hidden dimension may lead the legend but is never an axis).
export function pickSeriesDimension(rows, candidates, axisDims = candidates) {
  const hasPlainDim = [...axisDims, ...candidates].some((d) => !dateLike(d));
  let best = null;
  candidates.forEach((c, i) => {
    const labels = labelsOf(rows, c);
    const n = distinct(labels);
    if (n < 2 || n > MAX_AUTO_SERIES) return;                       // nothing to group / unreadable legend
    if (dateLike(c) && n > MAX_DATE_SERIES && hasPlainDim) return;  // a real time axis belongs on x
    // Renderers tell a pivoted cell from a leaf one by looking for `value` /
    // `rendered` on it, so a dimension whose value IS one of those words would
    // make the regrouped cell read as a number.
    if (labels.some((l) => LEAF_KEYS.has(l))) return;
    const xDims = axisDims.filter((d) => d.name !== c.name && !determinedBy(rows, c, d));
    if (!xDims.length) return;                                      // nothing left to put on the axis
    const xn = distinct(rows.map((r) => keyOf(r, xDims)));
    // Only regroup when the axis genuinely repeats today — that IS the bug. One
    // row per category is already a correct chart.
    if (xn < 2 || xn >= rows.length) return;
    const cand = { sDim: c, xDims, n, date: dateLike(c), at: i };
    if (!best || betterSeries(cand, best)) best = cand;
  });
  return best;
}

function betterSeries(a, b) {
  if (a.n !== b.n) return a.n < b.n;        // fewer bars per group
  if (a.date !== b.date) return !a.date;    // "Kappa 2026" makes a better legend than "2026-07-12"
  return a.at >= b.at;                      // ties to the trailing dimension — the one a person pivots by
}

// Returns { rows, dimensions, pivots, seriesDim } in Looker's pivoted shape, or
// null when the result should be drawn exactly as it arrived (a single
// dimension, no repeating axis, too many series…).
export function groupExtraDimension({ rows = [], dimensions = [], measures = [], pivots = [], hiddenDimensions = [] } = {}) {
  if (!rows.length || !measures.length) return null;
  const named = (dimensions || []).filter((d) => d?.name);
  const xable = rowDimensions(rows, named);
  if (!xable.length) return null;
  // A dimension that isn't in the rows (Looker pivoted it) must not be read as
  // the axis — narrowing the list is worth returning on its own.
  const narrowed = xable.length !== named.length
    ? { rows, dimensions: xable, pivots: pivots || [], seriesDim: null }
    : null;
  // The axis prints the same label twice today — combining the row dimensions
  // into one label is the fallback that keeps the promise when we can't group.
  const repeats = () => xable.length > 1 && distinct(labelsOf(rows, xable[0])) < rows.length;
  const combineOnly = () => ({
    rows: rows.map((r) => {
      const k = keyOf(r, xable);
      return { ...r, [COMBINED_X]: { value: k, rendered: k } };
    }),
    dimensions: [combinedDim(xable)],
    pivots: pivots || [],
    seriesDim: null,
  });

  // ── Already pivoted by Looker ───────────────────────────────────────────────
  // The measure cells are keyed by pivot already, so there is no second
  // dimension to fold in — but the ROW dimensions can still repeat the axis
  // (pivot on the event, rows by age group × ticket type), and that reads as
  // the same bug. Combine their labels instead.
  if ((pivots || []).length) return repeats() ? combineOnly() : narrowed;

  // Measure cells must be flat. A pivot-shaped cell without a `pivots` block is
  // somebody else's shape (a blend, a Pulse dataset) — don't touch it.
  if (measures.some((m) => rows.some((r) => r[m.name] != null && !isLeafCell(r[m.name])))) return narrowed;

  // The legend comes from a visible dimension where there is one. Failing that,
  // a dimension Looker HID from the vis may lead it: hiding a breakdown does
  // not stop it splitting the rows, and grouping by something the vis doesn't
  // list still beats printing the same category twice.
  const pick = pickSeriesDimension(rows, xable)
    || pickSeriesDimension(rows, rowDimensions(rows, (hiddenDimensions || []).filter((d) => d?.name)), xable);
  if (!pick) return repeats() ? combineOnly() : narrowed;

  const { sDim, xDims } = pick;
  const seriesKeys = [...new Set(labelsOf(rows, sDim))]; // first-appearance order = the query's sort
  const byX = new Map();
  for (const r of rows) {
    const k = keyOf(r, xDims);
    let out = byX.get(k);
    if (!out) {
      out = {};
      for (const d of xDims) out[d.name] = r[d.name];   // the original cell — keeps its rendered label AND its drill links
      if (xDims.length > 1) out[COMBINED_X] = { value: k, rendered: k };
      for (const m of measures) out[m.name] = {};
      byX.set(k, out);
    }
    const sk = text(r[sDim.name]);
    for (const m of measures) if (r[m.name] !== undefined) out[m.name][sk] = r[m.name];
  }

  return {
    rows: [...byX.values()],
    dimensions: xDims.length > 1 ? [combinedDim(xDims)] : xDims,
    // Same shape json_detail returns for a pivoted query, so the renderer can't
    // tell the difference: `key` is the series value, `data` names the field.
    pivots: seriesKeys.map((k) => ({ key: k, is_total: false, data: { [sDim.name]: k } })),
    seriesDim: sDim,
  };
}
